import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, HostBinding, inject, Injector, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { RouterModule } from '@angular/router';
import { GlobalDataService } from '@core/global-data';
import { BaseMixinComponent } from '@mixins/components/base-component';
import { TranslatableComponentMixin } from '@mixins/components/translatable';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingSpinner } from '@widgets/loading-spinner';
import { MirIcon } from '@widgets/mir-icon';
import { TooltipDirective } from '@widgets/tooltip';
import { WizardPanelComponent } from '@wizard/wizard-panel';
import { ShellController } from './services';
import { SettingsComponent } from './settings';
import { ShellHeader } from './shell-header';
import { ShellNavigation } from './shell-navigation';

const TRAD_BASE_KEY = `Wizard.UI.Shell`;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'mir-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    MatCardModule,
    ShellHeader,
    ShellNavigation,
    SettingsComponent,
    WizardPanelComponent,
    MirIcon,
    LoadingSpinner,
    TooltipDirective,
  ]
})
export class ShellComponent extends TranslatableComponentMixin(BaseMixinComponent) {

  @HostBinding('class.zenMode')
  get zenModeCssClass(): boolean { return this.isZenMode(); }

  @HostBinding('class.emphazeContent')
  get emphazeContentCssClass(): boolean { return this.isContentEmphazed(); }

  @HostBinding('class.error')
  get errorCssClass(): boolean { return this.hasError(); }

  /** @Inject */
  private readonly appDataService = inject(GlobalDataService);
  private readonly shellController = inject(ShellController);

  readonly hasUser = signal(false);
  readonly wizardHasBasket = signal(false);
  readonly wizardBasketIsEmpty = signal(true);
  readonly wizardHasBeenUpdated = signal(false);
  readonly infoIcon = signal('danger');
  readonly infoIconTooltip = signal('');
  readonly wizardHasBeenUpdatedFromBroadcastChannel = signal(false);
  readonly hasError: Signal<boolean>;
  readonly hasWizardFeature: Signal<boolean>;

  readonly isZenMode = computed(() => this.shellController.isZenMode());
  readonly isContentEmphazed = computed(() => this.shellController.isContentEmphazed());
  readonly isSettingsOpened = computed(() => this.shellController.isSettingsOpened());
  readonly isWizardPanelVisible = computed(() => this.shellController.isWizardPanelVisible());
  readonly isWizardPanelOpened = computed(() => this.shellController.isWizardPanelExpanded());
  readonly isNavigationExpanded = computed(() => this.shellController.isNavigationExpanded());

  constructor() {
    const injector = inject(Injector);

    super();

    this.hasError = toSignal(
      this.appDataService.onError$,
      { initialValue: false, injector }
    );

    this.hasWizardFeature = toSignal(
      this.shellController.observeFeature('ReferentialCompanyWizard'),
      { initialValue: false, injector }
    );

    effect(() => {
      this.hasUser.set(this.shellController.hasUser());
    });

    effect(() => {
      const wizardHasBasket = this.shellController.wizardHasBasket();
      const wizardBasketIsEmpty = this.shellController.wizardBasketIsEmpty();

      this.wizardHasBasket.set(wizardHasBasket);
      this.wizardBasketIsEmpty.set(wizardBasketIsEmpty);
      this.infoIconTooltip.set(
        this.translate(
          `${TRAD_BASE_KEY}.Tooltips.Informations.Has${wizardBasketIsEmpty ? 'Empty' : ''}Basket`
        )
      );
      this.infoIcon.set(wizardBasketIsEmpty ? 'warning' : 'info');
    });

    effect(() => {
      const wizardHasBeenUpdated = this.shellController.wizardHasBeenUpdated();
      this.wizardHasBeenUpdated.set(wizardHasBeenUpdated);
    });
  }

  handleReload() {
    this.appDataService.init();
  }

  handleToggleExpand() {
    this.shellController.toggleWizardPanel();
  }
}
